import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSection,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { NotificationBar } from '@/components/NotificationBar'
import {
  BulkBundlePriceList,
  BundlePriceItem,
} from '@/features/admin/bundle/components/BulkBundlePriceList'
import type { Id } from '@/infra/id'
import styles from './BulkApplyDiscountDialog.module.css'

export interface BulkApplyDiscountPreview {
  id: Id
  name: string
  currentPrice: number
  newPrice: number
}

export interface BulkApplyDiscountDialogProps {
  showing: boolean
  onClose: () => unknown
  /**
   * The discount form.
   */
  children?: ReactNode
  previews: BulkApplyDiscountPreview[]
  hasDiscount: boolean
  negativePriceWarning?: string | null
  hasEligibleBundles: boolean
  hasArchivedBundles: boolean
  archivedCount: number
  canApply: boolean
  isSubmitPending: boolean
  /**
   * The id of the discount form the footer's submit button submits.
   */
  formId: string
  testId?: string
}

export function BulkApplyDiscountDialog({
  showing,
  onClose,
  children,
  previews,
  hasDiscount,
  negativePriceWarning,
  hasEligibleBundles,
  hasArchivedBundles,
  archivedCount,
  canApply,
  isSubmitPending,
  formId,
  testId,
}: BulkApplyDiscountDialogProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Apply discount</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetSection spaced>{children}</ModalSheetSection>

        <BulkBundlePriceList rightHeader="New price">
          {previews.map((preview) => {
            const isNegative = preview.newPrice < 0

            return (
              <BundlePriceItem
                key={preview.id}
                name={preview.name}
                currentPrice={preview.currentPrice}
                testId={testId && `${testId}-preview-${preview.name}`}
                right={
                  hasDiscount && (
                    <span
                      className={
                        isNegative ? styles.negativePrice : styles.newPrice
                      }
                    >
                      {formatPrice(preview.newPrice)}
                      {isNegative && ' (below $0)'}
                    </span>
                  )
                }
              />
            )
          })}
        </BulkBundlePriceList>
      </ModalSheetContent>

      {negativePriceWarning && (
        <NotificationBar
          centered
          type="error"
          testId={testId && `${testId}-warning`}
        >
          {negativePriceWarning}
        </NotificationBar>
      )}

      <ModalSheetFooter>
        {!hasEligibleBundles ? (
          <ModalSheetFooterSection>
            All selected bundles are archived and can’t be discounted.
          </ModalSheetFooterSection>
        ) : (
          hasArchivedBundles && (
            <ModalSheetFooterSection>
              {archivedCount} archived{' '}
              {archivedCount === 1 ? 'bundle' : 'bundles'} excluded.
            </ModalSheetFooterSection>
          )
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
            testId={testId && `${testId}-close-button`}
          />

          <Button
            label="Apply discount"
            type="submit"
            form={formId}
            variant="primary"
            isPending={isSubmitPending}
            isDisabled={!canApply || isSubmitPending}
            testId={testId && `${testId}-submit-button`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

function formatPrice(value: number) {
  return value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`
}
